import React, { useMemo, useState } from "react";
import logoo from "../assets/logo.svg";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";

function Header({ onOpenAuth, onOpenMenu }) {
  const navigate = useNavigate();
  const { search, pathname } = useLocation();
  const params = new URLSearchParams(search);
  const initial = params.get("q") || "";
  const [value, setValue] = useState(initial);
  const [mobileUserMenuOpen, setMobileUserMenuOpen] = useState(false);
  const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
  const token = useSelector((state) => state.auth?.token);
  const isLoggedIn =
    Boolean(token) && token !== "null" && token !== "undefined";

  React.useEffect(() => {
    const handler = (e) => {
      const target = e?.target;
      if (!(target instanceof Element)) return;
      if (target.closest("[data-mobile-user-menu-root]")) return;
      setMobileUserMenuOpen(false);
    };
    document.addEventListener("pointerdown", handler);
    return () => document.removeEventListener("pointerdown", handler);
  }, []);


  const hamburgerIcon = useMemo(
    () => (
      <svg
        viewBox="0 0 24 24"
        className="h-5 w-5 shrink-0"
        fill="none"
        stroke="currentColor"
        strokeWidth="2.2"
        strokeLinecap="round"
      >
        <path d="M4 7h16" />
        <path d="M4 12h16" />
        <path d="M4 17h16" />
      </svg>
    ),
    []
  );

  React.useEffect(() => {
    const root = document.documentElement;
    root.classList.remove("dark");
    try {
      localStorage.setItem("femtyz_theme", "light");
    } catch {
      // ignore
    }
  }, []);

  function submitSearch(e) {
    e?.preventDefault();
    const q = value.trim();
    if (!q) {
      return;
    }
    navigate(`/search?q=${encodeURIComponent(q)}`);
    setMobileSearchOpen(false);
  }

  return (
    <header className="sticky top-0 z-50 border-b bg-[#F8FAFC] backdrop-blur">
      <div className="mx-auto w-full max-w-none border-2 border-[#E6EAF0] bg-[#F8FAFC] px-1 py-3 sm:px-4 md:px-5 lg:px-6">
        {/* Mobile header (matches screenshot style) */}
        <div className="md:hidden">
          <div className="mb-2 flex items-center justify-between gap-2">
            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onOpenMenu}
                aria-label="Open menu"
                className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#7352C7] shadow-sm ring-1 ring-[#E6EAF0] text-white"
              >
                {hamburgerIcon}
              </button>
              <Link to="/" className="flex shrink-0 items-center">
                <img src={logoo} className="h-8 w-auto max-w-[120px]" alt="FEMTYZ logo" />
              </Link>
            </div>

            <div className="flex items-center gap-2">
              <button
                type="button"
                aria-label={mobileSearchOpen ? "Close search" : "Open search"}
                onClick={() => setMobileSearchOpen((s) => !s)}
                className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#7352C7] text-white shadow-sm"
              >
                {mobileSearchOpen ? (
                  <svg
                    viewBox="0 0 24 24"
                    className="h-5 w-5 shrink-0 text-white"
                    fill="none"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                  >
                    <path d="M6 6l12 12" />
                    <path d="M18 6L6 18" />
                  </svg>
                ) : (
                  <svg
                    viewBox="0 0 24 24"
                    className="h-5 w-5 shrink-0 text-white"
                    fill="none"
                    stroke="white"
                    strokeWidth="2"
                    strokeLinecap="round"
                  >
                    <path d="M21 21l-4.35-4.35" />
                    <circle cx="11" cy="11" r="7" />
                  </svg>
                )}
              </button>

              <div className="relative" data-mobile-user-menu-root>
                <button
                  type="button"
                  aria-label="User"
                  className="inline-flex h-10 w-10 items-center justify-center rounded-xl bg-[#7352C7] text-white shadow-sm"
                  onClick={() => {
                    if (isLoggedIn) {
                      navigate("/settings");
                      return;
                    }
                    setMobileUserMenuOpen((s) => !s);
                  }}
                >
                  <svg
                    viewBox="0 0 24 24"
                    className="h-5 w-5"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                    strokeLinecap="round"
                  >
                    <circle cx="12" cy="7" r="4" />
                    <path d="M20 21a8 8 0 10-16 0" />
                  </svg>
                </button>

                {!isLoggedIn && mobileUserMenuOpen && (
                  <div className="absolute right-0 top-full z-50 mt-2 w-44 overflow-hidden rounded-xl border border-[#E6EAF0] bg-white shadow-lg">
                    <button
                      type="button"
                      className="block w-full px-4 py-2.5 text-left text-[14px] font-semibold text-[#24303A] hover:bg-slate-50"
                      onClick={() => {
                        setMobileUserMenuOpen(false);
                        onOpenAuth?.("login");
                      }}
                    >
                      Login
                    </button>
                    <div className="h-px bg-[#EEF2F7]" aria-hidden />
                    <button
                      type="button"
                      className="block w-full px-4 py-2.5 text-left text-[14px] font-semibold text-[#24303A] hover:bg-slate-50"
                      onClick={() => {
                        setMobileUserMenuOpen(false);
                        onOpenAuth?.("signup");
                      }}
                    >
                      Sign up
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className={`items-center gap-3 ${mobileSearchOpen ? "flex" : "hidden"}`}>
            <form onSubmit={submitSearch} className="flex min-w-0 flex-1 items-center">
              <div className="flex w-full min-w-0 items-center gap-2 rounded-full border border-[#E6EAF0] bg-white/70 px-3 py-2 shadow-sm">
            {/* <svg
              viewBox="0 0 24 24"
              className="h-5 w-5 shrink-0 text-[#7352C7]"
              fill="none"
              stroke="#7352C7"
              strokeWidth="2"
              strokeLinecap="round"
            >
              <path d="M21 21l-4.35-4.35" />
              <circle cx="11" cy="11" r="7" />
            </svg> */}
                <input
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  placeholder="Search, paste or compare"
                  className="min-w-0 w-full bg-transparent text-left text-base font-medium text-[#24303A] placeholder:text-center placeholder:text-base placeholder:text-[#6E7B86] focus:outline-none"
                />
              </div>
            </form>

          </div>
        </div>

        {/* Desktop header (existing) */}
        <div className="hidden md:flex items-center gap-3">
          <div className="flex items-center gap-2">
            <Link to="/" className="flex items-center gap-2">
              <img src={logoo} className="h-10 w-auto max-w-[140px]" alt="logo" />
            </Link>
          </div>

          <form onSubmit={submitSearch} className="flex flex-1 items-center justify-center">
            <div className="flex w-full max-w-[560px] items-center gap-2 rounded-full border border-[#E6EAF0] bg-white/70 px-3 py-2 shadow-sm md:px-4">
              <input
                value={value}
                onChange={(e) => setValue(e.target.value)}
                placeholder="Paste a list or search (e.g., Milk, Eggs, Bread)..."
                className="w-full bg-transparent text-base font-medium text-[#24303A] placeholder:text-base placeholder:text-[#6E7B86] focus:outline-none"
              />
              <button
                type="submit"
                className="rounded-full bg-[#7352C7] px-4 py-1.5 text-[16px] font-bold text-white"
              >
                Search
              </button>
            </div>
          </form>

          <div className="flex items-center gap-2">
            {isLoggedIn ? (
              <button
                type="button"
                aria-label="Profile"
                className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#7352C7] shadow-sm ring-1 ring-[#E6EAF0] text-white"
                onClick={() => navigate("/settings")}
              >
                <svg viewBox="0 0 24 24" className="h-5 w-5" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
                  <circle cx="12" cy="7" r="4" />
                  <path d="M20 21a8 8 0 10-16 0" />
                </svg>
              </button>
            ) : (
              <>
                <button
                  type="button"
                  className="rounded-full border-2 border-[#E6EAF0] shadow-sm bg-white px-6 py-1 text-[16px] font-bold font-semibold text-[#24303A] hover:border-indigo-500 hover:border-femty-primary"
                  onClick={() => onOpenAuth?.("login")}
                >
                  Sign in
                </button>
                <button
                  type="button"
                  className="rounded-full bg-[#7352C7] px-6 py-1 text-[16px] font-semibold text-white"
                  onClick={() => onOpenAuth?.("signup")}
                >
                  Sign up
                </button>
              </>
            )}

            <button
              type="button"
              onClick={onOpenMenu}
              aria-label="Open menu"
              className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-[#7352C7] shadow-sm ring-1 ring-[#E6EAF0] text-white"
            >
              {hamburgerIcon}
            </button>
          </div>
        </div>
      </div>
    </header>
  );
}

export default Header;